import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import {
  InsurancePolicyApiResponse,
  InsurancePolicyCreateRequest,
  InsurancePolicyFilters,
  InsurancePolicyQueryParams,
  InsurancePolicyStatistics,
  InsurancePolicyUpdateRequest,
} from '../../entities/insurance/insurance-policy-api.types';
import { InsurancePolicy } from '../../entities/insurance/insurance-policy.entities';
import { EntityCreatedResponse, PaginatedResponse } from '../../models/api/api-response.model';
import { BaseApiService } from '../api/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class InsurancePolicyService extends BaseApiService {

  /**
   * Get all insurance policies with pagination and optional filters
   */
  getPolicies(params?: InsurancePolicyQueryParams, filters?: InsurancePolicyFilters): Observable<PaginatedResponse<InsurancePolicy>> {
    // Get farm_id from filters - it's required for the path
    const farmId = filters?.farm_id;
    if (!farmId) {
      throw new Error('Farm ID is required to get insurance policies');
    }

    let httpParams = new HttpParams();

    // Add required search parameter (API requires 'q' parameter)
    if (params?.q && params.q.trim() !== '') {
      httpParams = httpParams.set('q', params.q.trim());
    }

    if (params) {
      if (params.skip !== undefined) {
        httpParams = httpParams.set('skip', params.skip.toString());
      }
      if (params.limit !== undefined) {
        httpParams = httpParams.set('limit', params.limit.toString());
      }
    }

    // Add other filters as query parameters (excluding farm_id which goes in path)
    if (filters) {
      if (filters.policy_type) {
        httpParams = httpParams.set('_policy_type', filters.policy_type);
      }
      if (filters.status) {
        httpParams = httpParams.set('_status', filters.status);
      }
      if (filters.provider) {
        httpParams = httpParams.set('_provider', filters.provider);
      }
    }

    const endpoint = `/farms/${farmId}/insurance/policies`;
    return this.getPaginated<InsurancePolicy>(endpoint, httpParams);
  }

  /**
   * Get insurance policy by ID
   */
  getPolicyById(id: string, farmId: string): Observable<InsurancePolicyApiResponse> {
    return this.get<InsurancePolicyApiResponse>(`/farms/${farmId}/insurance/policies/${id}`);
  }

  /**
   * Create new insurance policy
   */
  createPolicy(policy: InsurancePolicyCreateRequest, farmId: string): Observable<EntityCreatedResponse> {
    return this.post<EntityCreatedResponse>(`/farms/${farmId}/insurance/policies`, policy);
  }

  /**
   * Update insurance policy
   */
  updatePolicy(id: string, updates: InsurancePolicyUpdateRequest, farmId: string): Observable<void> {
    return this.put<void>(`/farms/${farmId}/insurance/policies/${id}`, updates);
  }

  /**
   * Patch insurance policy (partial update)
   */
  patchPolicy(id: string, updates: Partial<InsurancePolicyUpdateRequest>, farmId: string): Observable<void> {
    return this.patch<void>(`/farms/${farmId}/insurance/policies/${id}`, updates);
  }

  /**
   * Delete insurance policy
   */
  deletePolicy(id: string, farmId: string): Observable<void> {
    return this.delete<void>(`/farms/${farmId}/insurance/policies/${id}`);
  }

  /**
   * Get policies by farm ID
   */
  getPoliciesByFarm(farmId: string, params?: InsurancePolicyQueryParams): Observable<PaginatedResponse<InsurancePolicy>> {
    return this.getPolicies(params, { farm_id: farmId });
  }

  /**
   * Get policies by type
   */
  getPoliciesByType(type: string, params?: InsurancePolicyQueryParams): Observable<PaginatedResponse<InsurancePolicy>> {
    return this.getPolicies(params, { policy_type: type });
  }

  /**
   * Get policies by status
   */
  getPoliciesByStatus(status: string, params?: InsurancePolicyQueryParams): Observable<PaginatedResponse<InsurancePolicy>> {
    return this.getPolicies(params, { status });
  }

  /**
   * Get policies by provider
   */
  getPoliciesByProvider(provider: string, params?: InsurancePolicyQueryParams): Observable<PaginatedResponse<InsurancePolicy>> {
    return this.getPolicies(params, { provider });
  }

  /**
   * Get policy statistics for a farm
   */
  getPolicyStatistics(farmId: string): Observable<InsurancePolicyStatistics> {
    return this.get<InsurancePolicyStatistics>(`/farms/${farmId}/insurance/statistics`);
  }
}
